import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  MessageSquare, Search, Filter, BarChart, Plus,
  AlertCircle, CheckCircle, Clock, ChevronRight,
  FileText, Download, Send, Paperclip, HelpCircle,
  Globe, ArrowRight, Star, Users, Settings, Phone,
  Mail, Calendar, ArrowUpRight, ArrowDownRight,
  Inbox, Archive, Trash, XCircle
} from 'lucide-react';
import ChatBot from '../../../components/ChatBot';

const translations = {
  es: {
    title: 'Sistema de Soporte',
    subtitle: 'Gestiona tus tickets de soporte y solicitudes de ayuda',
    sections: {
      overview: 'Vista General',
      myTickets: 'Mis Tickets',
      knowledge: 'Base de Conocimiento',
      catalog: 'Catálogo de Servicios'
    },
    actions: {
      newTicket: 'Nuevo Ticket',
      downloadReport: 'Descargar Reporte',
      submitReply: 'Enviar Respuesta'
    },
    search: 'Buscar tickets...',
    filters: 'Filtros',
    status: {
      open: 'Abierto',
      inProgress: 'En Progreso',
      resolved: 'Resuelto',
      closed: 'Cerrado'
    },
    priority: {
      low: 'Baja',
      medium: 'Media',
      high: 'Alta',
      urgent: 'Urgente'
    },
    form: {
      title: 'Título del ticket',
      description: 'Descripción del problema',
      priority: 'Prioridad',
      category: 'Categoría',
      attachments: 'Adjuntar archivos'
    }
  },
  en: {
    title: 'Support System',
    subtitle: 'Manage your support tickets and help requests',
    sections: {
      overview: 'Overview',
      myTickets: 'My Tickets',
      knowledge: 'Knowledge Base',
      catalog: 'Service Catalog'
    },
    actions: {
      newTicket: 'New Ticket',
      downloadReport: 'Download Report',
      submitReply: 'Submit Reply'
    },
    search: 'Search tickets...',
    filters: 'Filters',
    status: {
      open: 'Open',
      inProgress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    },
    priority: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent'
    },
    form: {
      title: 'Ticket title',
      description: 'Problem description',
      priority: 'Priority',
      category: 'Category',
      attachments: 'Attach files'
    }
  }
};

// Mock data
const mockTickets = [
  {
    id: 'INC0010001',
    title: 'Cannot access email',
    description: 'Getting error when trying to access company email',
    category: 'IT Support',
    priority: 'high',
    status: 'open',
    createdAt: '2025-03-14T10:30:00',
    updatedAt: '2025-03-14T11:15:00',
    assignee: 'IT Support Team',
    responses: [
      {
        id: 1,
        author: 'IT Support',
        content: 'We are investigating the issue. Have you tried clearing your browser cache?',
        timestamp: '2025-03-14T11:15:00'
      }
    ]
  },
  {
    id: 'INC0010002',
    title: 'Request for new monitor',
    description: 'Need a second monitor for development work',
    category: 'Hardware Request',
    priority: 'medium',
    status: 'in-progress',
    createdAt: '2025-03-13T15:45:00',
    updatedAt: '2025-03-14T09:00:00',
    assignee: 'IT Hardware Team',
    responses: [
      {
        id: 1,
        author: 'IT Hardware',
        content: 'Your request has been approved. We will deliver the monitor tomorrow.',
        timestamp: '2025-03-14T09:00:00'
      }
    ]
  }
];

const mockKnowledgeBase = [
  {
    id: 'KB0001',
    title: 'How to reset your password',
    category: 'Account Management',
    views: 1234,
    helpful: 156,
    lastUpdated: '2025-03-01'
  },
  {
    id: 'KB0002',
    title: 'Accessing shared drives',
    category: 'File Management',
    views: 987,
    helpful: 89,
    lastUpdated: '2025-03-05'
  }
];

const mockCatalog = [
  {
    id: 'CAT001',
    title: 'Hardware Request',
    description: 'Request new or replacement hardware',
    icon: Settings,
    categories: ['Laptops', 'Monitors', 'Accessories']
  },
  {
    id: 'CAT002',
    title: 'Software Access',
    description: 'Request access to software applications',
    icon: Globe,
    categories: ['Development Tools', 'Office Apps', 'Design Software']
  }
];

const SupportPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [showChatbot, setShowChatbot] = useState(false);
  const [newTicketModal, setNewTicketModal] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  const stats = {
    openTickets: 3,
    avgResponseTime: '2h 15m',
    satisfaction: 95,
    resolvedToday: 12
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
            <p className="text-foreground/70">{t.subtitle}</p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowChatbot(true)}
              className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              AI Assistant
            </button>
            <button
              onClick={() => setNewTicketModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t.actions.newTicket}
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="dark-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-foreground/70">Open Tickets</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">{stats.openTickets}</h3>
              </div>
              <Inbox className="h-8 w-8 text-accent" />
            </div>
            <div className="mt-2">
              <span className="text-green-500 text-sm flex items-center">
                <ArrowDownRight className="h-4 w-4 mr-1" />
                -2 from yesterday
              </span>
            </div>
          </div>

          <div className="dark-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-foreground/70">Avg. Response Time</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">{stats.avgResponseTime}</h3>
              </div>
              <Clock className="h-8 w-8 text-accent" />
            </div>
            <div className="mt-2">
              <span className="text-green-500 text-sm flex items-center">
                <ArrowDownRight className="h-4 w-4 mr-1" />
                15min improvement
              </span>
            </div>
          </div>

          <div className="dark-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-foreground/70">Satisfaction Rate</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">{stats.satisfaction}%</h3>
              </div>
              <Star className="h-8 w-8 text-accent" />
            </div>
            <div className="mt-2">
              <span className="text-green-500 text-sm flex items-center">
                <ArrowUpRight className="h-4 w-4 mr-1" />
                +2% this week
              </span>
            </div>
          </div>

          <div className="dark-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-foreground/70">Resolved Today</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">{stats.resolvedToday}</h3>
              </div>
              <CheckCircle className="h-8 w-8 text-accent" />
            </div>
            <div className="mt-2">
              <span className="text-green-500 text-sm flex items-center">
                <ArrowUpRight className="h-4 w-4 mr-1" />
                +3 from yesterday
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Tickets List */}
          <div className="lg:col-span-2">
            <div className="dark-card">
              {/* Search and Filters */}
              <div className="p-4 border-b border-border">
                <div className="flex space-x-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder={t.search}
                      className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-foreground/50" />
                  </div>
                  <button className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
                    <Filter className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Tickets */}
              <div className="divide-y divide-border">
                {mockTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="p-4 hover:bg-secondary/30 cursor-pointer"
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`h-2 w-2 rounded-full ${
                          ticket.priority === 'high' ? 'bg-red-500' :
                          ticket.priority === 'medium' ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}></div>
                        <span className="ml-2 text-sm font-medium text-foreground">
                          {ticket.id}
                        </span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        ticket.status === 'open' ? 'bg-blue-100 text-blue-800' :
                        ticket.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {t.status[ticket.status]}
                      </span>
                    </div>

                    <h3 className="mt-2 text-lg font-medium text-foreground">
                      {ticket.title}
                    </h3>
                    <p className="mt-1 text-sm text-foreground/70">
                      {ticket.description}
                    </p>

                    <div className="mt-2 flex items-center justify-between text-sm">
                      <div className="flex items-center text-foreground/70">
                        <Clock className="h-4 w-4 mr-1" />
                        {new Date(ticket.createdAt).toLocaleString()}
                      </div>
                      <div className="flex items-center text-foreground/70">
                        <Users className="h-4 w-4 mr-1" />
                        {ticket.assignee}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="dark-card p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-secondary/30 text-foreground">
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-accent mr-3" />
                    <span>Contact Support</span>
                  </div>
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-secondary/30 text-foreground">
                  <div className="flex items-center">
                    <HelpCircle className="h-5 w-5 text-accent mr-3" />
                    <span>Browse FAQ</span>
                  </div>
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-secondary/30 text-foreground">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-accent mr-3" />
                    <span>Schedule Meeting</span>
                  </div>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Knowledge Base */}
            <div className="dark-card p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Popular Articles</h3>
              <div className="space-y-4">
                {mockKnowledgeBase.map((article) => (
                  <div key={article.id} className="space-y-2">
                    <h4 className="text-foreground hover:text-accent cursor-pointer">
                      {article.title}
                    </h4>
                    <div className="flex items-center text-sm text-foreground/70">
                      <Star className="h-4 w-4 mr-1" />
                      <span>{article.helpful} found helpful</span>
                      <span className="mx-2">•</span>
                      <span>{article.views} views</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Service Catalog */}
            <div className="dark-card p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Service Catalog</h3>
              <div className="space-y-4">
                {mockCatalog.map((service) => (
                  <div key={service.id} className="space-y-2">
                    <div className="flex items-center">
                      <service.icon className="h-5 w-5 text-accent mr-2" />
                      <h4 className="text-foreground">{service.title}</h4>
                    </div>
                    <p className="text-sm text-foreground/70">{service.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {service.categories.map((category, index) => (
                        <span
                          key={index}
                          className="text-xs px-2 py-1 rounded-full bg-secondary/30 text-foreground/70"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chatbot */}
      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role="support"
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}

      {/* New Ticket Modal */}
      {newTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-border">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-foreground">
                  {t.actions.newTicket}
                </h3>
                <button
                  onClick={() => setNewTicketModal(false)}
                  className="text-foreground/50 hover:text-foreground"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-1">
                    {t.form.title}
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="Enter ticket title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-1">
                    {t.form.description}
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="Describe your issue"
                  ></textarea>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-1">
                      {t.form.category}
                    </label>
                    <select className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent">
                      <option>IT Support</option>
                      <option>Hardware Request</option>
                      <option>Software Access</option>
                      <option>Network Issues</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-1">
                      {t.form.priority}
                    </label>
                    <select className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent">
                      <option value="low">{t.priority.low}</option>
                      <option value="medium">{t.priority.medium}</option>
                      <option value="high">{t.priority.high}</option>
                      <option value="urgent">{t.priority.urgent}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-1">
                    {t.form.attachments}
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-border border-dashed rounded-lg">
                    <div className="space-y-1 text-center">
                      <Paperclip className="mx-auto h-12 w-12 text-foreground/30" />
                      <div className="flex text-sm text-foreground/70">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer rounded-md font-medium text-accent hover:text-accent/90"
                        >
                          <span>Upload a file</span>
                          <input id="file-upload" name="file-upload" type="file" className="sr-only" />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-foreground/50">
                        PNG, JPG, PDF up to 10MB
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-4">
                <button
                  onClick={() => setNewTicketModal(false)}
                  className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30"
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90"
                >
                  Submit Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPage;